import { z } from "zod";

export const gmailApprovalSchema = z.object({
  execution_id: z.string().min(1),
  plan_hash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  state_revision: z.string().regex(/^[1-9][0-9]*$/),
  resource_ids: z.array(z.string().min(1)).max(100),
  status: z.enum(["planned", "approval_required", "running", "succeeded", "failed", "canceled"]),
  delivery_status: z.enum(["", "pending", "accepted", "retryable_failed"]),
  decision: z.enum(["", "approved", "rejected"]),
  from: z.string(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  subject: z.string(),
  text: z.string().max(131072),
  can_confirm: z.boolean(),
  can_cancel: z.boolean(),
  blocking_code: z.string().max(100),
  expires_at: z.string().datetime({ offset: true }),
  error_code: z.string().max(100),
  error_category: z.string().max(100),
  follow_up: z.boolean(),
}).strict();
export const gmailApprovalsResponseSchema = z.object({
  schema: z.literal("conversation_gmail_approval.v1"),
  approvals: z.array(gmailApprovalSchema),
}).strict();
export const gmailDecisionResponseSchema = z.object({
  operation_id: z.string().min(1), execution_id: z.string().min(1),
  status: z.enum(["pending", "accepted", "retryable_failed"]),
}).strict();
export type GmailApproval = z.infer<typeof gmailApprovalSchema>;

export function gmailApprovalError(code: string) {
  const messages: Readonly<Record<string, string>> = {
    gmail_provider_rejected: "Gmail 拒绝了发送请求，尚未确认发送成功。",
    access_token_missing: "发送凭据缺失，请重新授权 Gmail。",
    gmail_token_refresh_failed: "Gmail 授权更新失败，请重新授权。",
    gmail_connection_ambiguous: "存在多个发件账号，请重新明确选择账号。",
    critical_target_resource_ids_required: "邮件计划未绑定明确收件人，请重新准备邮件。",
    execution_plan_blocked: "邮件计划未通过执行前置检查，请重新准备邮件。",
    required_body_param_missing: "邮件缺少必要内容，请补齐后重新准备邮件。",
    approval_timeout: "等待确认已超时，请重新准备邮件。",
    graph_ambiguous: "发送能力没有唯一匹配，请重新准备邮件。",
    graph_contract_incomplete: "发送能力合同不完整，暂时无法执行。",
    gmail_approval_expired: "这份邮件方案已过期，请重新准备邮件。",
    gmail_reauthorization_required: "发件账号需要重新授权 Gmail，尚未发送。",
    gmail_connection_missing: "发件账号未连接或已解除授权，尚未发送。",
    gmail_from_mismatch: "发件账号与当前绑定邮箱不一致，请重新准备邮件。",
    gmail_target_required: "缺少明确的收件人或审批目标，请补齐后重新准备邮件。",
    gmail_approval_plan_unsupported: "这份旧方案无法提供完整邮件预览，请重新准备邮件。",
    gmail_approval_preview_unavailable: "无法完整读取这份邮件内容，请重新准备邮件。",
    approval_permission_denied: "当前账号没有确认发送权限。",
    approval_version_conflict: "邮件方案已变化，请重新读取后确认。",
    approval_resources_changed: "收件人已变化，请重新读取后确认。",
    approval_already_accepted: "这份方案已有确认结果，请读取最新状态。",
    approval_dispatch_failed: "审批提交未完成，尚未确认发送；请读取状态后重试。",
  };
  return messages[code] ?? "邮件状态暂时无法确认，请重新读取；不会自动发送。";
}

export function gmailFailureReason(item: GmailApproval) {
  const categories: Readonly<Record<string, string>> = {
    permission_or_scope: "Gmail 授权或发送权限不足，请重新授权。",
    provider_auth: "Gmail 授权已失效或凭据不可用，请重新授权。",
    rate_limit_retry: "Gmail 配额不足或请求受限，请稍后重新准备发送。",
    platform_internal: "Gmail 服务暂时异常，尚未确认发送成功。",
    missing_information: "邮件请求缺少必要内容或格式不正确，请重新准备邮件。",
    network: "发送时网络异常，尚未确认发送成功。",
  };
  return categories[item.error_category] ?? (item.error_code && item.error_code !== "unexpected_error" ? gmailApprovalError(item.error_code) : "尚未取得明确失败原因，请读取最新状态；不要重复发送同一封邮件。");
}
